import { useCallback, useContext, useEffect, useReducer, useRef, useState } from "react";
import { API_URL } from "config";
import { useLocation, useNavigate } from "react-router";
import { SuccessAlertContext } from "App";
import { Box, Button, SpeedDial, SxProps, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Notify from "config/notify";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import ERROR_MESSAGE from "constants/message";
import { localEnv } from "features/order/constants";
import { HelpOutline } from "@mui/icons-material";
import SettingSearchDialog from "../components/SettingSearchDialog";
import CompanyTable from "../components/CompanyTable";
import CompanyRegisterModal from "../components/CompanyRegisterModal";
import useCompanySetting from "../hooks/useCompanySetting";
import useCompanySearch from "../hooks/useCompanySearch";
import downloadAllDataApi from "../api/downloadAllDataApi";
import { RegisterCompanyArgs } from "../types";
import { removeEmptyValues } from "../utils/common";
import downloadHelpPdf from "../api/downloadHelpPdf";

/**
 * ジョイフル企業用設定画面
 * @returns JSX.Element
 */
export default function JoyfulSetting() {
  const styles: { [key: string]: SxProps } = {
    buttonContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 0.5,
      paddingTop: 1,
      position: "relative",
    },
    leftButtonContainer: {
      position: "absolute",
      top: 0,
      left: 0,
    },
  };

  const [hasChanges, setHasChanges] = useState(false);
  const [isOpenSettingSearch, setIsOpenSettingSearch] = useState(false);
  const [isOpenCompanyRegister, setIsOpenCompanyRegister] = useState(false);
  const [isOpenCompanyEdit, setIsOpenCompanyEdit] = useState(false);
  const [isOpenCompanyDelete, setIsOpenCompanyDelete] = useState(false);
  const [selectedCompanyCd, setSelectedCompanyCd] = useState<string>("");
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // 追加ローディング処理
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const [isScrollTop, setIsScrollTop] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigate = useNavigate();

  const { showAlert } = useContext(SuccessAlertContext);

  const location = useLocation();
  const isAuthorizedUser = useRef<boolean>(false);

  // ユーザーが適切かチェック
  const checkAuthorized = useCallback(() => {
    console.log("checkAuthorized");

    const userType = localStorage.getItem("user_type");
    console.log("checkAuthorized userType:", userType);
    if(!localEnv) {
      if (!(userType === "C0009")) {
        console.log("userType empty");
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: "ジョイフルユーザーで再度ログインしてください。",
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
        return false;
      } if (checkAccessTokenExpired()) {
        console.log("token expired");
        // 期限切れなら
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
        return false;
      }
    }
    // 認証ユーザーがOKなら
    isAuthorizedUser.current = true;
    return true;
  }, [isAuthorizedUser.current]);

  // const handleCsvDialogToggle = () => {
  //   setIsOpenStockCsvUpload(!isOpenStockCsvUpload);
  // };

  // サンプルCSVダウンロード処理
  const onClickDownloadCsv = async () => {
    // const response = await fetch(`${API_URL}/joyful/item/csv/download`, { method: "get" });
    // const blob = await response.blob();
    // const url = window.URL.createObjectURL(blob);
    // const a = document.createElement("a");
    // a.href = url;
    // a.download = "sample.csv";
    // a.click();
    await downloadAllDataApi(showAlert);
  };

  // 契約企業絞込検索APIを呼び出す
  const { total, companies, editedStores, error, isSearching, isMoreSearching, isFastLoading, setEditedStores, fetchData, setParams, setInit, fetchMoreData } =
    useCompanySearch({}, isAuthorizedUser.current);

  // 契約企業登録、更新、削除APIを呼び出す
  const {
    isRegistering,
    isUpdating,
    isDeleting,
    setIsRegistering,
    registerCompany,
    updateCompany,
    deleteCompany,
  } = useCompanySetting();

  const [searchName, setSearchName] = useState<string>("");
  const [searchCd, setSearchCd] = useState<string>("");
  const [searchCenterCd, setSearchCenterCd] = useState<string>("");

  const handleRegister = async (params: RegisterCompanyArgs) => {
    await registerCompany(removeEmptyValues({
      company_cd: params.companyCd,
      center_cd: params.centerCd,
      stores: params.stores,
    }));
    fetchData(removeEmptyValues({
      limit: 10,
      offset: 0,
      company_cd: searchCd,
      company_name: searchName,
      center_cd: searchCenterCd,
    }));
  };

  const handleUpdate = async (params: RegisterCompanyArgs) => {
    await updateCompany(removeEmptyValues({
      company_cd: params.companyCd,
      center_cd: params.centerCd,
      stores: params.stores,
    }));
    fetchData(removeEmptyValues({
      limit: 10,
      offset: 0,
      company_cd: searchCd,
      company_name: searchName,
      center_cd: searchCenterCd,
    }));
  };

  const handleDelete = async () => {
    await deleteCompany(selectedCompanyCd);
    fetchData(removeEmptyValues({
      limit: 10,
      offset: 0,
      company_cd: searchCd,
      company_name: searchName,
      center_cd: searchCenterCd,
    }));
  };

  // 企業リスト検索用hooks
  const handleSearch = async () => {
    setParams((prev) => removeEmptyValues({
      ...prev,
      offset: 0,
      company_cd: searchCd,
      company_name: searchName,
      center_cd: searchCenterCd,
    }));
    fetchData(removeEmptyValues({
      limit: 10,
      offset: 0,
      company_cd: searchCd,
      company_name: searchName,
      center_cd: searchCenterCd,
    }));
  };

  /*   // ページを切り替えるときに、オフセットを次のページに進める
  const onPageChange = (_: any, value: number) => {
    setPage(value);
    refetch({
      limit: rowsPerPage,
      offset: (value - 1) * rowsPerPage,
    });
  }; */

  const handleRegisterDialogToggle = () => {
    setIsOpenCompanyRegister(!isOpenCompanyRegister);
  };
  const handleSearchDialogToggle = () => {
    setIsOpenSettingSearch(!isOpenSettingSearch);
  };

  const handleTableChange = (changesDetected: boolean) => {
    setHasChanges(changesDetected);
  };

  // スクロール処理
  const handleScroll = async () => {
    setIsScrollTop(window.pageYOffset > 0);

    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current) {
      return;
    }
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      console.log("scroll bottom");
      isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await fetchMoreData();
    })();
  }, [fireMoreLoadEvent]);

  useEffect(() => {
    (async () => {
      setInit(true);
    })();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const isAuthed = checkAuthorized();
      if (isAuthed) forceUpdate()
    }, 0);
  }, [location.pathname]);

  return (
    <Box position="relative" width="90%" alignContent="flex-start" ml="5%" minHeight={330} pt={4}>
      {/* header */}
      <Box position="absolute" top={32} left={0} width="100%" textAlign="center">
        <Typography fontSize={22}>ジョイフル用設定（企業一覧）</Typography>
      </Box>
      <Box width="100%" height={32} textAlign="right" mt={-4}>
        <Button
          variant="custom-outlined"
          onClick={async (e) => {
            // apiからpdfのURLを取得
            const url = await downloadHelpPdf(`${getUserType()}/resources/joyful/manual_joyful.pdf`);
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          startIcon={<HelpOutline color="secondary" />}
          sx={{ height: 24, padding: 1, marginTop: 0.5, marginRight: 1 }}
        >
          ヘルプ
        </Button>
      </Box>
      <Box sx={styles.buttonContainer}>
        <Box sx={styles.leftButtonContainer}>
          <Box mx={1}>
            <Button
              variant="custom-outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => navigate(-1)}
            >
              戻る
            </Button>
          </Box>
        </Box>

        <Box mx={0.2}>
          <Button variant="contained" onClick={handleRegisterDialogToggle}>
            <Box>企業登録</Box>
          </Button>
        </Box>
        <Box mx={0.2}>
          <Button variant="contained" onClick={handleSearchDialogToggle}>
            <Box>絞込検索</Box>
          </Button>
        </Box>
        <Box mx={0.2}>
          <Button variant="contained" onClick={() => onClickDownloadCsv()}>
            全データCSVダウンロード
          </Button>
        </Box>
      </Box>

      {/* table */}
      <CompanyTable
        isOpenCompanyEdit={isOpenCompanyEdit}
        setIsOpenCompanyEdit={setIsOpenCompanyEdit}
        isOpenCompanyDelete={isOpenCompanyDelete}
        setIsOpenCompanyDelete={setIsOpenCompanyDelete}
        stores={editedStores}
        total={total}
        companies={companies}
        onChange={handleTableChange}
        handleUpdate={handleUpdate}
        handleDelete={handleDelete}
        setCommitStores={setEditedStores}
        setSelectedCompanyCd={setSelectedCompanyCd}
        isLoading={isSearching}
        isMoreLoading={isMoreSearching}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        error={error}
      />
      {/* modals */}
      <CompanyRegisterModal
        open={isOpenCompanyRegister}
        isSearching={isSearching}
        isRegistering={isRegistering}
        onClose={() => setIsOpenCompanyRegister(false)}
        onRegister={handleRegister}
        bgColor="#FBE5D6"
      />
      <SettingSearchDialog
        open={isOpenSettingSearch}
        onClose={() => setIsOpenSettingSearch(false)}
        onSearch={handleSearch}
        companyCd={searchCd}
        companyName={searchName}
        centerCd={searchCenterCd}
        setCompanyCd={setSearchCd}
        setCompanyName={setSearchName}
        setCenterCd={setSearchCenterCd}
        isLoading={isSearching}
      />
      {isScrollTop && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <SpeedDial
            ariaLabel="Back to top"
            icon={<KeyboardArrowUpIcon />}
            onClick={scrollToTop}
            open // 常に展開済みにしてアイコンだけ表示
            direction="up" // 展開方向は上だがActionは不要
            FabProps={{ size: "small" }}
          />
        </Box>
      )}
    </Box>
  );
}
